import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import Countdown from 'react-countdown'
import { AnimatePresence, motion } from 'framer-motion'

import { useSocket } from 'hooks'
import { animatedFadeDown, animatedFadeUp } from 'helpers/animations'

import { Button as DefaultButton, CloseIcon } from 'components'
import { default as DefaultModal } from 'components/AuthModal/components/Modal'
import Overlay from 'components/AuthModal/components/Overlay'
import TimerCircle from 'components/EventTimer/TimerCircle'

import { OVERLAY } from 'components/AuthModal/styles'

const transition = { duration: 0.3, ease: 'easeInOut' }
const fadeDown = {
  ...animatedFadeDown,
  transition,
}

const fadeUp = {
  ...animatedFadeUp,
  transition,
}

const POLL_STATES = {
  EMPTY: 'EMPTY',
  ACTIVE: 'ACTIVE',
  QUESTION: 'QUESTION',
  ROUND_END: 'ROUND_END',
  END: 'END',
}

const getCountdownRenderer =
  max =>
  ({ seconds, milliseconds }) =>
    (
      <TimerCircle
        max={max}
        value={seconds}
        percent={((seconds + milliseconds / 1000) / max) * 100}
        showProgressIndicator={true}
      />
    )

const Button = props => (
  <DefaultButton className="bg-frolyPink text-base font-normal w-44 rounded-3xl" {...props} />
)

const Options = ({ options, selectedOption, onOptionSelect, roundEnd }) => (
  <div>
    {options.map(option => (
      <button
        key={option.id}
        className={classnames(
          ' relative overflow-hidden block text-left w-full bg-sharkBlack mb-3 rounded-full py-4 text-base px-6 transition-opacity duration-1000',
          {
            'border-2 border-solid border-azureBlue': selectedOption === option.id,
            'hover:opacity-80': !roundEnd,
            'cursor-default': roundEnd,
          }
        )}
        onClick={() => (roundEnd ? null : onOptionSelect(option.id))}
      >
        <div className="relative z-10 text-base">{option.text}</div>
        <div
          className="absolute bg-azureBlue h-full inset-0 transition-width"
          style={{
            width: `${option.answerPercent || 0}%`,
          }}
        />
      </button>
    ))}
  </div>
)

const DailyPoints = ({ show, onClose, rewards }) => {
  const [pollState, setPollState] = useState()
  const [poll, setPoll] = useState()
  const [question, setQuestion] = useState()
  const [results, setResults] = useState()
  const socket = useSocket('/polls', !!pollState)

  useEffect(() => {
    const availableReward = rewards?.find(({ redeemed, id }) => id === 'polls' && !redeemed)
    if (availableReward && !pollState) {
      setPollState(POLL_STATES.ACTIVE)
    }
  }, [rewards, pollState])

  useEffect(() => {
    if (!socket || !pollState || poll) {
      return
    }

    socket.on('details', setPoll)
    socket.on('question', question => {
      question.endTime = new Date()
      question.endTime.setSeconds(question.endTime.getSeconds() + question.duration)
      setQuestion(question)
      setPollState(POLL_STATES.QUESTION)
    })

    socket.on('roundEnd', results => {
      setQuestion(question => ({
        ...question,
        ...results,
      }))
      setPollState(POLL_STATES.ROUND_END)
    })

    socket.on('end', results => {
      setResults(results)
      setPollState(POLL_STATES.END)
    })
  }, [socket, pollState, poll])

  const { title, subtitle, cta, info, inProgress } = useMemo(() => {
    if (!poll) {
      return {}
    }

    const inProgress = ![POLL_STATES.ACTIVE, POLL_STATES.END].includes(pollState)

    switch (pollState) {
      case POLL_STATES.ACTIVE: {
        return {
          title: poll.title,
          subtitle: poll.description,
          info: (
            <div className="text-base opacity-70 text-center">
              <div>
                {poll.totalPoints} points for {poll.noQuestions} questions
              </div>
              <div>Answer a min of {poll.minAnswers}</div>
            </div>
          ),
          inProgress,
          cta: 'Start',
        }
      }
      case POLL_STATES.END: {
        return {
          title: 'Congratulations',
          subtitle: `You have answered ${results.completed} of ${poll.noQuestions} questions. You have earned ${results.points} points`,
          cta: 'The end',
          inProgress,
        }
      }
      default:
        return { inProgress }
    }
  }, [poll, pollState, results])

  const handleStartOrEndPoll = useCallback(() => {
    if (pollState === POLL_STATES.ACTIVE) {
      socket.emit('question')
      return
    }

    socket.disconnect()
    onClose(true)
    setPollState()
  }, [pollState, socket, onClose])

  const handleOptionSelect = useCallback(
    id => {
      if (pollState !== POLL_STATES.QUESTION) {
        return
      }

      socket.emit('answer', { id })
    },
    [socket, pollState]
  )

  const handleNextQuestion = useCallback(() => {
    socket.emit('question')
  }, [socket])

  if (!pollState || !poll) {
    return null
  }

  return (
    <Overlay
      show={show}
      className={OVERLAY}
      preventEscBackdropClose={true}
      onClose={() => onClose()}
    >
      <DefaultModal innerClassName="overflow-y-scroll flex flex-col justify-center px-5">
        <div className="relative h-auto flex flex-col items-center text-white w-full rounded-xl px-5 py-6 bg-woodsmokeBlackDarker md:w-108">
          {inProgress ? (
            <div className="w-full">
              <AnimatePresence>
                {pollState === POLL_STATES.QUESTION && (
                  <motion.div className="flex items-center justify-center mb-2" {...fadeDown}>
                    <Countdown
                      date={question.endTime}
                      renderer={getCountdownRenderer(question.duration)}
                      key={question.endTime}
                      intervalDelay={500}
                      precision={3}
                      onComplete={() => setTimeout(() => handleOptionSelect(), 1000)}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
              <div className="text-lg mb-2 font-semibold text-left mb-8">
                {question.sn}. {question.text}
              </div>
              <div>
                <Options
                  options={question.options}
                  onOptionSelect={handleOptionSelect}
                  roundEnd={pollState === POLL_STATES.ROUND_END}
                />
              </div>
              <AnimatePresence>
                {pollState === POLL_STATES.ROUND_END && (
                  <motion.div className="flex flex-col justify-end h-24 items-center" {...fadeUp}>
                    <Button onClick={handleNextQuestion}>
                      {question.hasNext ? 'Next' : 'See results'}
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <>
              <div className="w-full flex justify-end">
                <button
                  className="bg-white bg-opacity-20 w-5 h-5 mb-2 rounded-full flex items-center justify-center"
                  onClick={onClose}
                >
                  <CloseIcon className="w-2 h-2 text-white" />
                </button>
              </div>
              <div className="text-xl mb-2 font-semibold">{title}</div>
              <div className="text-base opacity-80 mb-8 text-center mb-16">{subtitle}</div>
              {info && <div className="mb-6">{info}</div>}
              <Button onClick={handleStartOrEndPoll}>{cta}</Button>
            </>
          )}
        </div>
      </DefaultModal>
    </Overlay>
  )
}

DailyPoints.propTypes = {
  show: PropTypes.bool,
  rewards: PropTypes.array,
  onClose: PropTypes.func.isRequired,
}

export default DailyPoints
